import { useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import clsx from "clsx";
import TooltipMarkdown from "@/components/ui/TooltipMarkdown";
import { FloatingArrow, Placement, arrow, autoUpdate, flip, offset, shift, useFloating, useHover, useInteractions } from "@floating-ui/react";
import { createPortal } from "react-dom";
import { setRef } from "../utils/mergeRefs";

/** Fade duration (ms) — must match the `tooltip-fade` animation / transition. */
const TOOLTIP_FADE_MS = 150;

export type UseToolTipOptions = {
  text: string | React.ReactNode;
  placement?: Placement;
  textClassName?: string;
  addTextClassName?: string;
};

/**
 * A ref callback that also works as a ref-factory for merging multiple refs.
 *
 * Usage:
 *   // Simple — use directly as ref
 *   <button ref={ref} />
 *
 *   // Merged — pass other refs to get a combined callback ref
 *   <button ref={ref(otherRef, anotherRef)} />
 */
export type MergeableRef<T> = ((el: T | null) => void) & {
  (...otherRefs: React.Ref<T>[]): (el: T | null) => void;
};



export type UseToolTipResult<T extends Element = Element> = {
  /** Attach to the trigger element. Can also be called with other refs to merge. */
  ref: MergeableRef<T>;
  /** Spread onto the trigger element to enable hover detection. */
  hoverProps: Record<string, unknown>;
  /** Render this alongside the trigger element (portals to document.body). */
  tooltip: React.ReactNode;
};

/**
 * Hook-based tooltip — attaches directly to any element without a wrapper div.
 *
 * @example
 * const { ref, hoverProps, tooltip } = useToolTip({ text: "Click to edit" });
 *
 * // No other refs needed:
 * <Button ref={ref} {...hoverProps} />
 * {tooltip}
 *
 * // Merging with another ref:
 * <Button ref={ref(otherRef)} {...hoverProps} />
 * {tooltip}
 */
export function useToolTip<T extends Element = Element>(
  options: UseToolTipOptions | string | React.ReactNode
): UseToolTipResult<T> {
  const opts: UseToolTipOptions = typeof options === "string" || (options !== null && typeof options === "object" && !("text" in (options as object)))
    ? { text: options as React.ReactNode }
    : options as UseToolTipOptions;

  const [show, setShow] = useState(false);
  const arrowRef = useRef<SVGSVGElement>(null);

  // Fade-out support. A CSS keyframe handles fade-IN (it replays on every
  // mount), but nothing can animate an unmount — so when `show` goes false we
  // keep the node mounted for one fade duration (`exiting`) while the opacity
  // transition runs, then let it unmount. `wasShownRef` prevents mounting a
  // stray invisible node before the first real hover.
  const [exiting, setExiting] = useState(false);
  const wasShownRef = useRef(false);
  useEffect(() => {
    if (show) {
      setExiting(false);
      wasShownRef.current = true;
      return;
    }
    if (!wasShownRef.current) return;
    setExiting(true);
    wasShownRef.current = false;
    const timer = setTimeout(() => setExiting(false), TOOLTIP_FADE_MS);
    return () => clearTimeout(timer);
  }, [show]);

  const { refs, floatingStyles, context } = useFloating({
    open: show,
    onOpenChange: setShow,
    strategy: "fixed",
    placement: opts.placement ?? "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [
      arrow({ element: arrowRef }),
      offset({ mainAxis: 10 }),
      flip({ padding: 40 }),
      shift(),
    ],
  });

  const hover = useHover(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  const tooltipClass =
    (opts.textClassName ?? "bg-black px-2 py-1 text-white rounded min-w-[min-content] whitespace-nowrap") +
    (opts.addTextClassName ?? "");

  // Build the mergeable ref
  const internalRef = refs.setReference as (el: T | null) => void;

  // The ref MUST be referentially stable. React invokes a changed ref callback
  // with `null` (detach) then the node (attach) on every render; FloatingUI's
  // `setReference` calls `setDomReference` (a state update) each time, so an
  // unstable ref produced a detach→setState→re-render→detach… loop — the
  // "tooltip flickers on every hover" bug. Memoized on `internalRef` so a
  // re-render reuses the same callback. (The `<ToolTip>` component gets this for
  // free because it passes `refs.setReference` directly.)
  const mergeableRef = useMemo(
    () => Object.assign(
      (el: T | null) => { internalRef(el); },
      function (...otherRefs: React.Ref<T>[]) {
        return (el: T | null) => {
          internalRef(el);
          otherRefs.forEach(r => setRef(r, el));
        };
      }
    ) as MergeableRef<T>,
    [internalRef]
  );

  const tooltip = typeof window !== "undefined"
    ? createPortal(
        (show || exiting) && (
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className={clsx(
              "z-[9999] pointer-events-none transition-opacity",
              show ? "animate-tooltip-fade" : "opacity-0",
            )}
          >
            <div className={tooltipClass}>
              <FloatingArrow ref={arrowRef} context={context} />
              {typeof opts.text === "string" ? (
                <TooltipMarkdown className="max-w-[50dvw]">
                  {opts.text}
                </TooltipMarkdown>
              ) : opts.text}
            </div>
          </div>
        ),
        document.body
      )
    : null;

  return {
    ref: mergeableRef,
    hoverProps: getReferenceProps() as Record<string, unknown>,
    tooltip,
  };
}
